import { createContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { defaults } from "../../features/query/services/check-match";
import type { LoginProps, LoginResponse } from "../../features/user/types/user";
import * as api from "../lib/api";
import { cookies } from "../lib/cookies";

interface AuthContextData {
	signed: boolean;
	user: LoginProps | null;
	Login(user: object): Promise<boolean | "RECAPTCHA_V2_REQUIRED">;
	Logout(): void;
	Forgot(data: object): Promise<boolean>;
	ChangePassword(data: object): Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function AuthProvider({ children }: any) {
	const initialUser = (() => {
		const cookieUser = cookies.get("@Checkauto:user");
		return cookieUser ? (cookieUser as LoginProps) : null;
	})();

	const [user, setUser] = useState<LoginProps | null>(initialUser);

	function defaultCookiesSession() {
		setUser(null);
		cookies.remove("@Checkauto:user");
		cookies.remove("@Checkauto:access");
		cookies.remove("@Checkauto:refresh");
	}

	async function Login(
		userData: object,
	): Promise<boolean | "RECAPTCHA_V2_REQUIRED"> {
		const toastId = toast.loading("Aguarde...", {
			position: toast.POSITION.TOP_RIGHT,
		});

		try {
			const response = (await api.post("token/", userData)) as {
				status: number;
				data: LoginResponse;
			};
			toast.dismiss(toastId);

			if (response.status === 200) {
				const data = response.data as LoginProps;
				setUser(data);
				api.defaults.headers!.Authorization = `Bearer ${data.access}`;
				defaults.headers.Authorization = `Token ${data.checkmatch_token}`;
				cookies.set("@Checkauto:user", JSON.stringify(data));
				cookies.set("@Checkauto:access", data.access);
				cookies.set("@Checkauto:refresh", data.refresh);

				return true;
			} else if (response.data.code === "recaptcha_fallback_required") {
				toast.error(
					response.data.detail || "Verificação adicional necessária",
					{
						position: toast.POSITION.TOP_RIGHT,
					},
				);
				return "RECAPTCHA_V2_REQUIRED";
			} else {
				toast.error(response.data.detail || "Erro ao realizar login", {
					position: toast.POSITION.TOP_RIGHT,
				});
			}
		} catch (error: any) {
			toast.dismiss(toastId);
			toast.error(error.data?.detail || "Erro ao realizar login", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}

		return false;
	}

	async function Logout() {
		defaultCookiesSession();
	}

	useEffect(() => {
		const cookieUser = cookies.get("@Checkauto:user");
		const cookieTokenAccess = cookies.get("@Checkauto:access");
		const cookieTokenRefresh = cookies.get("@Checkauto:refresh");

		if (cookieTokenAccess && cookieTokenRefresh && cookieUser) {
			try {
				setUser(cookieUser);
				api.defaults.headers!.Authorization = `Bearer ${cookieTokenAccess}`;
				defaults.headers.Authorization = `Token ${cookieUser.checkmatch_token}`;
			} catch (err) {
				console.error("Erro ao parsear user do cookie", err);
				defaultCookiesSession();
			}
		}
	}, []);

	async function Forgot(data: object) {
		toast.loading("Aguarde...", {
			position: toast.POSITION.TOP_RIGHT,
		});

		let ok = false;

		await api.post("user/forgot-password/", data).then((response: any) => {
			toast.dismiss();
			if (response.status === 200) {
				toast.success(response.data.message, {
					position: toast.POSITION.TOP_RIGHT,
				});
				ok = true;
			}
		});

		return ok;
	}

	type dataChangePasswordProps = {
		current_password: string;
		new_password: string;
		new_password_again: string;
	};

	async function ChangePassword(data: dataChangePasswordProps) {
		if (data.new_password !== data.new_password_again) {
			toast.error(
				"Campos de 'Nova senha' e 'Repita a nova senha' devem ser idênticos!",
				{
					position: toast.POSITION.TOP_RIGHT,
				},
			);
			return;
		}

		toast.loading("Aguarde...", {
			position: toast.POSITION.TOP_RIGHT,
		});

		const response = (await api.post("user/change-password/", data)) as {
			[key: string]: any;
		};
		toast.dismiss();

		if (response.status === 200) {
			toast.success(
				"Senha atualizada com sucesso! Redirecionando, aguarde...",
				{
					position: toast.POSITION.TOP_RIGHT,
				},
			);
			setTimeout(() => {
				defaultCookiesSession();
				window.location.reload();
			}, 5000);
		} else if (response.status === 400) {
			toast.error(
				"A nova senha informada não atende os critérios de senha forte, ela deverá conter no mínimo: 8 caracteres, uma letra maiuscula, uma letra minuscula, um número e um símbolo.",
				{
					position: toast.POSITION.TOP_RIGHT,
				},
			);
		} else {
			toast.error(
				"Ocorreu um erro ao tentar alterar a senha do usuário, tente novamente mais tarde",
				{
					position: toast.POSITION.TOP_RIGHT,
				},
			);
			setTimeout(() => {
				defaultCookiesSession();
				window.location.reload();
			}, 5000);
		}
	}

	return (
		<AuthContext.Provider
			value={{
				signed: Boolean(user),
				user,
				Login,
				Logout,
				Forgot,
				ChangePassword,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export default AuthContext;
